<template>
  <v-card class="pa-0 transparent-bg" color="transparent" elevation="0">
    <v-row v-if="loading" class="py-12" justify="center">
      <v-progress-circular indeterminate color="red accent-2" size="64" width="6"></v-progress-circular>
    </v-row>

    <div v-else>
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-card class="glass-card glow-red rounded-xl pa-5 fill-height">
            <div class="red--text text--accent-1 text-overline font-weight-black tracking-widest">GLOBAL CHURN</div>
            <div class="d-flex align-baseline">
              <div class="text-h2 font-weight-black white--text mt-1">{{ globalStats.mainContracts }}</div>
              <div class="ml-3 text-h5 grey--text text--lighten-1 font-weight-light">{{ globalStats.churnRate }}%</div>
            </div>
            <div class="d-flex align-center mt-2 justify-space-between">
              <v-chip x-small color="rgba(255,82,82,0.2)" class="red--text text--accent-1 font-weight-bold px-2">Hauptverträge</v-chip>
              <span class="caption grey--text">Ø {{ globalStats.avgMembers }} Mitglieder</span>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="glass-card glow-cyan rounded-xl pa-5 fill-height">
            <div class="cyan--text text--accent-2 text-overline font-weight-black tracking-widest">ADD-ON DROP</div>
            <div class="text-h2 font-weight-black white--text mt-1">{{ globalStats.addOns }}</div>
            <div class="d-flex align-center mt-2">
              <v-chip x-small color="rgba(24,255,255,0.2)" class="cyan--text text--accent-2 font-weight-bold px-2">Alle Pakete</v-chip>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="glass-card glow-purple rounded-xl pa-5 fill-height">
            <div class="purple--text text--accent-2 text-overline font-weight-black tracking-widest">#1 GLOBALER GRUND</div>
            <div class="text-h5 font-weight-bold white--text mt-2 text-truncate">{{ globalStats.topReason.name }}</div>
            <div class="mt-2">
              <v-progress-linear
                :value="globalStats.topReason.percent"
                color="purple accent-2"
                background-color="rgba(255,255,255,0.1)"
                height="6"
                rounded></v-progress-linear>
              <div class="d-flex justify-space-between mt-1 caption grey--text">
                <span>{{ globalStats.topReason.count }}x genannt</span>
                <span>{{ globalStats.topReason.percent }}%</span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-2 mb-6">
        <v-col cols="12">
          <v-card class="glass-card rounded-xl pa-6 elevation-10" style="background: rgba(40, 40, 40, 0.85) !important">
            <div class="d-flex align-center mb-6 flex-wrap">
              <div class="mr-6 mb-2">
                <h3 class="text-h5 font-weight-bold white--text">Studio Analyse</h3>
                <div class="grey--text text-subtitle-2">
                  <span v-if="!showDowngradesOnly">Wähle einen Standort für Details</span>
                  <span v-else class="cyan--text text--accent-2 font-weight-bold">FILTER AKTIV: Zeige nur reine Paket-Kündigungen (Downgrades)</span>
                </div>
              </div>
              <div style="min-width: 300px; flex-grow: 1; max-width: 500px">
                <v-autocomplete
                  v-model="selectedStudio"
                  :items="studioList"
                  label="Studio suchen..."
                  outlined
                  dense
                  rounded
                  filled
                  dark
                  hide-details
                  prepend-inner-icon="mdi-map-marker"
                  color="red accent-2"
                  item-color="red accent-2"
                  placeholder="Alle Studios"
                  clearable></v-autocomplete>
              </div>
            </div>

            <v-divider class="mb-6 rgba-white-1"></v-divider>

            <v-row>
              <v-col cols="12" md="7">
                <div class="d-flex justify-space-between align-start mb-4">
                  <div>
                    <div class="text-h6 white--text font-weight-bold">
                      {{ selectedStudio || "Alle Studios" }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ showDowngradesOnly ? "Gefilterte Ansicht: Nur Downgrades" : "Vollständige Übersicht" }}
                    </div>
                  </div>

                  <v-card
                    @click="toggleDowngradeFilter"
                    v-ripple
                    :color="showDowngradesOnly ? 'rgba(0, 229, 255, 0.25)' : 'rgba(0,229,255,0.1)'"
                    class="pa-3 rounded-lg d-flex align-center cursor-pointer transition-swing"
                    outlined
                    :style="
                      showDowngradesOnly
                        ? 'border: 2px solid #00E5FF !important; transform: scale(1.02); box-shadow: 0 0 15px rgba(0,229,255,0.3);'
                        : 'border-color: #00E5FF !important'
                    ">
                    <div class="mr-3">
                      <div class="text-caption cyan--text text--accent-2 font-weight-bold text-uppercase">Nur Paket gekündigt</div>
                      <div class="text-caption grey--text" style="font-size: 10px !important">
                        {{ showDowngradesOnly ? "Klick zum Zurücksetzen" : "Klick zum Filtern" }}
                      </div>
                    </div>
                    <div class="text-h4 font-weight-black white--text">
                      {{ studioStatsKPI.downgrades }}
                    </div>
                    <v-icon right :color="showDowngradesOnly ? 'white' : 'cyan accent-2'">
                      {{ showDowngradesOnly ? "mdi-filter-check" : "mdi-filter-outline" }}
                    </v-icon>
                  </v-card>
                </div>

                <div class="rounded-lg pa-4 custom-scroll" style="background: rgba(0, 0, 0, 0.2); max-height: 450px; overflow-y: auto">
                  <div class="d-flex justify-space-between text-overline grey--text mb-2 px-1">
                    <span>Artikel {{ showDowngradesOnly ? "(Nur Downgrades)" : "" }}</span>
                    <span>Anzahl</span>
                  </div>

                  <div v-if="filteredViewData.allProducts.length === 0" class="grey--text font-italic caption pa-4 text-center">
                    Keine Daten für diese Auswahl.
                  </div>

                  <template v-for="(item, i) in filteredViewData.allProducts">
                    <div :key="i" class="mb-3 hover-row pa-2 rounded">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <div class="d-flex align-center" style="overflow: hidden">
                          <v-icon x-small :color="item.isMain ? 'red accent-2' : 'cyan accent-3'" class="mr-2">
                            {{ item.isMain ? "mdi-account-remove" : "mdi-package-variant-minus" }}
                          </v-icon>
                          <span class="text-body-2 white--text font-weight-medium text-truncate" :title="item.name">{{ item.name }}</span>
                        </div>

                        <div class="d-flex align-center shrink">
                          <v-chip x-small :color="item.isMain ? 'red accent-2' : 'cyan accent-3'" class="black--text font-weight-bold ml-2" label>
                            {{ item.count }}
                          </v-chip>
                        </div>
                      </div>
                      <v-progress-linear
                        :value="(item.count / filteredViewData.maxProductCount) * 100"
                        :color="item.isMain ? 'red accent-2' : 'cyan accent-3'"
                        background-color="rgba(255,255,255,0.05)"
                        height="4"
                        rounded></v-progress-linear>
                    </div>
                  </template>
                </div>
              </v-col>

              <v-col cols="12" md="5">
                <div class="text-h6 white--text font-weight-bold mb-1">Gründe</div>
                <div class="text-caption grey--text mb-4">
                  {{ showDowngradesOnly ? "Warum werden Pakete einzeln gekündigt?" : "Warum kündigen Kunden hier?" }}
                </div>

                <div class="d-flex justify-center align-center" style="min-height: 250px">
                  <apexchart
                    ref="reasonChart"
                    type="donut"
                    width="100%"
                    height="300"
                    :options="dynamicReasonOptions"
                    :series="dynamicReasonSeries"></apexchart>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card class="glass-card rounded-xl pa-5 fill-height elevation-4">
            <div class="d-flex justify-space-between align-center mb-4">
              <h3 class="text-h6 font-weight-bold white--text">Zeitlicher Verlauf</h3>
              <div class="d-flex gap-3">
                <template v-if="!showDowngradesOnly">
                  <div class="d-flex align-center">
                    <div class="dot red accent-2 mr-2"></div>
                    <span class="caption white--text">Haupt</span>
                  </div>
                </template>
                <div class="d-flex align-center">
                  <div class="dot cyan accent-3 mr-2"></div>
                  <span class="caption white--text">Paket</span>
                </div>
              </div>
            </div>
            <apexchart type="area" height="300" :options="trendChartOptions" :series="trendSeries"></apexchart>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-6">
        <v-col cols="12">
          <v-card class="glass-card rounded-xl pa-0 elevation-4 overflow-hidden">
            <v-toolbar color="transparent" flat height="80">
              <div class="d-flex flex-column">
                <span class="text-h6 white--text font-weight-bold">
                  {{ showDowngradesOnly ? "Downgrade Liste" : "Detail-Log" }}
                </span>
                <span class="text-caption grey--text">
                  Filter:
                  <strong :class="showDowngradesOnly ? 'cyan--text text--accent-2' : 'white--text'">
                    {{ selectedStudio || "Alle Studios" }} {{ showDowngradesOnly ? "+ Nur Downgrades" : "" }}
                  </strong>
                </span>
              </div>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Liste durchsuchen..."
                single-line
                hide-details
                dense
                filled
                rounded
                background-color="rgba(255,255,255,0.07)"
                dark
                style="max-width: 250px"></v-text-field>
            </v-toolbar>

            <v-data-table
              :headers="tableHeaders"
              :items="filteredTableItems"
              :search="search"
              dark
              class="transparent-table px-4 pb-4"
              :items-per-page="10"
              sort-by="terminatedDate"
              sort-desc>
              <template v-slot:item.terminatedDate="{ item }">
                <span class="grey--text text--lighten-2">{{ formatDate(item.terminatedDate) }}</span>
              </template>
              <template v-slot:item.contractType="{ item }">
                <v-chip v-if="item.isMain" x-small color="red accent-2" class="font-weight-bold black--text">Haupt</v-chip>
                <v-chip
                  v-else
                  x-small
                  color="cyan accent-4"
                  outlined
                  class="font-weight-bold"
                  style="border-color: #00e5ff !important; color: #00e5ff !important"
                  >Zusatz</v-chip
                >
              </template>
              <template v-slot:item.fullName="{ item }">
                <span class="white--text font-weight-bold">{{ item.fullName }}</span>
              </template>
              <template v-slot:item.studioName="{ item }">
                <v-chip x-small color="grey darken-3">{{ item.studioName }}</v-chip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import api from "@/api"
import moment from "moment"

export default {
  name: "CancellationDashboard",
  props: {
    studios: { type: Array, default: () => [] },
    datePickerRange: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      rawData: [],
      search: "",
      selectedStudio: null,
      showDowngradesOnly: false, // NEW STATE

      tableHeaders: [
        { text: "Datum", value: "terminatedDate", width: "100px" },
        { text: "Typ", value: "contractType", width: "90px" },
        { text: "Name", value: "fullName" },
        { text: "Produkt", value: "itemName" },
        { text: "Grund", value: "reason" },
        { text: "Studio", value: "studioName" },
      ],

      trendSeries: [],
      trendChartOptions: {
        chart: { type: "area", stacked: true, toolbar: { show: false }, background: "transparent" },
        theme: { mode: "dark" },
        colors: ["#FF5252", "#00E5FF"],
        fill: { type: "gradient", gradient: { opacityFrom: 0.6, opacityTo: 0.1 } },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { type: "datetime", labels: { style: { colors: "#9ca3af" } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { style: { colors: "#9ca3af" } } },
        grid: { borderColor: "rgba(255,255,255,0.05)", strokeDashArray: 4 },
        legend: { show: false },
        tooltip: { theme: "dark" },
      },
    }
  },
  computed: {
    studioList() {
      const set = new Set(this.allDetailsFlat.map((d) => d.studioName))
      return Array.from(set).sort()
    },

    allDetailsFlat() {
      const uniqueMap = new Map()
      this.rawData.forEach((day) => {
        if (day.details) {
          day.details.forEach((d) => {
            const uid = `${d.customerId}_${d.itemName}_${moment(d.terminatedDate).format("YYYYMMDD")}`
            if (!uniqueMap.has(uid)) {
              const type = d.contractType || "Paket"
              uniqueMap.set(uid, {
                ...d,
                studioName: day.studio ? day.studio.name : "Unbekannt",
                contractType: type,
                isMain: type === "Hauptvertrag",
              })
            }
          })
        }
      })
      return Array.from(uniqueMap.values())
    },

    globalStats() {
      const flat = this.allDetailsFlat
      const main = flat.filter((x) => x.isMain).length

      let totalMembersSum = 0,
        days = 0
      this.rawData.forEach((d) => {
        if (d.totalMembers > 0) {
          totalMembersSum += d.totalMembers
          days++
        }
      })
      const avgMembers = days > 0 ? Math.round(totalMembersSum / days) : 0

      const reasons = {}
      flat.forEach((x) => {
        const r = x.reason || "N/A"
        reasons[r] = (reasons[r] || 0) + 1
      })
      const sortedReasons = Object.entries(reasons).sort((a, b) => b[1] - a[1])

      return {
        mainContracts: main,
        addOns: flat.length - main,
        avgMembers,
        churnRate: avgMembers > 0 ? ((main / avgMembers) * 100).toFixed(2) : 0,
        topReason: sortedReasons.length
          ? { name: sortedReasons[0][0], count: sortedReasons[0][1], percent: ((sortedReasons[0][1] / flat.length) * 100).toFixed(1) }
          : { name: "-", count: 0, percent: 0 },
      }
    },

    // 1. DATA PREPARATION: Base Subset (Based on Studio Selection ONLY)
    baseSubset() {
      if (this.selectedStudio) {
        return this.allDetailsFlat.filter((x) => x.studioName === this.selectedStudio)
      }
      return this.allDetailsFlat
    },

    // 2. DATA PREPARATION: Determine Downgrades
    processedBaseData() {
      const subset = this.baseSubset
      const churnCustomerIds = new Set()
      subset.forEach((d) => {
        if (d.isMain) churnCustomerIds.add(d.customerId)
      })

      return subset.map((d) => {
        // Is this record a "True Downgrade"?
        // (Not Main Contract AND User did NOT cancel Main Contract in this period)
        const isTrueDowngrade = !d.isMain && !churnCustomerIds.has(d.customerId)
        return { ...d, isTrueDowngrade }
      })
    },

    // 3. STATS FOR KPI CARD (Static, does not change when filtered)
    studioStatsKPI() {
      const data = this.processedBaseData
      const downgrades = data.filter((d) => d.isTrueDowngrade).length
      return { downgrades }
    },

    // 4. FILTERED DATA FOR VIEW (Lists, Charts, Table)
    filteredTableItems() {
      const data = this.processedBaseData
      if (this.showDowngradesOnly) {
        return data.filter((d) => d.isTrueDowngrade)
      }
      return data
    },

    // 5. STATS FOR VIEW (Products & Reasons)
    filteredViewData() {
      const subset = this.filteredTableItems // Uses the filtered data!

      // Products
      const productCounts = {}
      subset.forEach((d) => {
        const n = d.itemName || "Unbekannt"
        if (!productCounts[n]) productCounts[n] = { count: 0, isMain: d.isMain }
        productCounts[n].count++
      })
      const allProducts = Object.entries(productCounts)
        .map(([name, val]) => ({ name, count: val.count, isMain: val.isMain }))
        .sort((a, b) => b.count - a.count)

      // Reasons
      const reasonCounts = {}
      subset.forEach((d) => {
        const r = d.reason || "N/A"
        reasonCounts[r] = (reasonCounts[r] || 0) + 1
      })
      const topReasons = Object.entries(reasonCounts)
        .map(([name, val]) => ({ name, value: val }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5)

      return {
        allProducts,
        maxProductCount: allProducts.length ? allProducts[0].count : 1,
        topReasons,
      }
    },

    dynamicReasonSeries() {
      return this.filteredViewData.topReasons.map((r) => r.value)
    },
    dynamicReasonOptions() {
      return {
        chart: { type: "donut", background: "transparent" },
        theme: { mode: "dark" },
        labels: this.filteredViewData.topReasons.map((r) => r.name),
        colors: ["#7C4DFF", "#FF4081", "#00E5FF", "#FFAB00", "#64DD17"],
        stroke: { width: 0 },
        plotOptions: { pie: { donut: { size: "70%", labels: { show: true, total: { show: true, label: "Gesamt", color: "#fff" } } } } },
        legend: { position: "bottom", show: true, labels: { colors: "#aaa" }, itemMargin: { horizontal: 5, vertical: 2 } },
        dataLabels: { enabled: false },
      }
    },
  },
  watch: {
    studios: {
      deep: true,
      handler() {
        this.fetchData()
      },
    },
    datePickerRange: {
      deep: true,
      handler() {
        this.fetchData()
      },
    },
    showDowngradesOnly() {
      this.updateTrendChart()
    }, // Re-render chart on toggle
    selectedStudio() {
      this.showDowngradesOnly = false // Reset filter on studio change
      this.updateTrendChart()
    },
  },
  mounted() {
    if (this.studios.length) this.fetchData()
  },
  methods: {
    formatDate(d) {
      return moment(d).format("DD.MM.YY")
    },
    toggleDowngradeFilter() {
      this.showDowngradesOnly = !this.showDowngradesOnly
    },

    async fetchData() {
      if (!this.studios.length || !this.datePickerRange.length) return
      this.loading = true
      try {
        const res = await api.fetchCancellationReport({
          range: this.datePickerRange,
          studios: this.studios.map((s) => s._id),
        })
        this.rawData = res
        this.updateTrendChart()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    updateTrendChart() {
      const dateMap = {}
      const dataToChart = this.filteredTableItems // Uses filtered data

      dataToChart.forEach((d) => {
        const date = moment(d.terminatedDate).format("YYYY-MM-DD")
        const ts = moment(d.terminatedDate).valueOf()
        if (!dateMap[date]) dateMap[date] = { ts, main: 0, addon: 0 }
        d.isMain ? dateMap[date].main++ : dateMap[date].addon++
      })
      const sorted = Object.keys(dateMap).sort()

      this.trendSeries = [
        { name: "Haupt", data: sorted.map((k) => [dateMap[k].ts, dateMap[k].main]) },
        { name: "Zusatz", data: sorted.map((k) => [dateMap[k].ts, dateMap[k].addon]) },
      ]
    },
  },
}
</script>

<style scoped>
.glass-card {
  background: rgba(30, 30, 30, 0.6) !important;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
}
.glow-red {
  border-left: 4px solid #ff5252 !important;
  background: linear-gradient(135deg, rgba(255, 82, 82, 0.15) 0%, rgba(30, 30, 30, 0.6) 50%) !important;
}
.glow-cyan {
  border-left: 4px solid #00e5ff !important;
  background: linear-gradient(135deg, rgba(0, 229, 255, 0.15) 0%, rgba(30, 30, 30, 0.6) 50%) !important;
}
.glow-purple {
  border-left: 4px solid #e040fb !important;
  background: linear-gradient(135deg, rgba(224, 64, 251, 0.15) 0%, rgba(30, 30, 30, 0.6) 50%) !important;
}
.rgba-white-1 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Interactive Elements */
.cursor-pointer {
  cursor: pointer;
}
.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

/* Scrollbar */
.custom-scroll::-webkit-scrollbar {
  width: 4px;
}
.custom-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}
.custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}
.hover-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

/* Table Reset */
.transparent-table >>> table {
  background: transparent !important;
}
.transparent-table >>> th {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: #9ca3af !important;
  font-size: 0.75rem;
  text-transform: uppercase;
}
.transparent-table >>> td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}
.tracking-widest {
  letter-spacing: 2px !important;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
</style>
